<script setup lang="ts">
import { computed } from 'vue';
import Button from '@/components/ui/Button.vue';
import BrandMark from '@/components/ui/BrandMark.vue';

// Set by the server when it turns an account away — see AdminController.AccessDenied and the
// Google handler's OnRemoteFailure in Program.cs, both of which land back here with a reason
// rather than on a bare error page.
const reason = computed(() => new URLSearchParams(window.location.search).get('auth'));

const message = computed(() => {
  switch (reason.value) {
    case 'denied':
      return 'That Google account has not been invited to this panel. Ask an existing Admin to invite it, then try again.';
    case 'failed':
      return 'Sign-in did not complete. Try again.';
    case 'signedout':
      return 'You have been signed out.';
    default:
      return null;
  }
});

const tone = computed(() => (reason.value === 'signedout' ? 'neutral' : 'warning'));
</script>

<template>
  <div class="flex min-h-screen items-center justify-center bg-paper px-6">
    <div class="w-full max-w-sm rounded-card border border-line bg-surface p-8">
      <div class="flex flex-col items-center gap-3 text-center">
        <BrandMark :size="32" />
        <h1 class="type-title">PostNoor Admin</h1>
        <p class="type-body text-ink-2">Sign in with the Google account you were invited with.</p>
      </div>

      <p
        v-if="message"
        class="type-caption mt-6 rounded-control border px-4 py-3"
        :class="tone === 'warning' ? 'border-warning-mark/40 bg-warning-bg text-warning-fg' : 'border-line bg-sunken text-ink-2'"
      >
        {{ message }}
      </p>

      <!--
        A real top-level navigation, not a fetch(): /auth/login issues the OAuth challenge, which
        302s on to accounts.google.com — a cross-origin hop no fetch() can follow.
      -->
      <Button href="/auth/login" variant="primary" class="mt-6 w-full">
        <svg class="size-4" viewBox="0 0 18 18" aria-hidden="true">
          <path
            fill="#4285F4"
            d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z"
          />
          <path
            fill="#34A853"
            d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18Z"
          />
          <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33Z" />
          <path
            fill="#EA4335"
            d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.47.89 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58Z"
          />
        </svg>
        Sign in with Google
      </Button>
    </div>
  </div>
</template>
